/* The Atelier: Klaviyo back in stock on the product page. <at-klaviyo-bis> is rendered by snippets/klaviyo-bis.liquid
   (only when a variant is sold out and the product is not tagged hide_cta); the account id, copy and font URLs come
   from its data-* attributes. The trigger button ("Notify me when back in stock") lives in snippets/buy-buttons.liquid
   with an inline display:none that Klaviyo's onsite.js toggles for a sold-out variant.
   - onsite.js (105 KB) loads on the first touch of the size options, or at once when data-eager is set (the URL pins
     a sold-out variant or the whole product is sold out).
   - The trigger's visibility is mirrored onto product-info.at-bis-active (CSS hides Add to bag) and announced with an
     at:bis event, which assets/at-product.js uses for the phone bar's "Notify me".
   - Once the modal opens its variant <select> is rebuilt as size pills, and the modal (an iframe) is restyled.
   The theme's deferred klaviyo.js (sign-up forms, tracking) is separate and does not provide back in stock; see load().
   It cannot be exercised on 127.0.0.1 (onsite.js drops the port); use the hosted preview. */
if (!customElements.get('at-klaviyo-bis')) {
  customElements.define(
    'at-klaviyo-bis',
    class AtKlaviyoBis extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('product-info');
        this.trigger = this.section && this.section.querySelector('.klaviyo-bis-trigger');
        this.form = this.trigger && this.trigger.closest('form');
        if (!this.trigger || !this.form) return;

        this.sync = this.sync.bind(this);
        this.load = this.load.bind(this);
        this.onFirstTouch = this.onFirstTouch.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.onNotify = this.onNotify.bind(this);
        this.section.addEventListener('click', this.onNotify, true);

        // Klaviyo toggles the trigger's inline display; one observer on that attribute is the only signal it gives.
        this.observer = new MutationObserver(this.sync);
        this.observer.observe(this.trigger, { attributes: true, attributeFilter: ['style'] });
        this.sync();

        if (this.dataset.pills === 'true') document.addEventListener('click', this.onOpen);
        if (this.dataset.eager === 'true') {
          this.load();
        } else {
          this.touchEvents = ['pointerdown', 'focusin', 'change'];
          this.touchEvents.forEach((type) => this.section.addEventListener(type, this.onFirstTouch, { passive: true }));
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('click', this.onOpen);
        if (this.section) this.section.removeEventListener('click', this.onNotify, true);
        this.removeTouchListeners();
      }

      removeTouchListeners() {
        if (!this.touchEvents) return;
        this.touchEvents.forEach((type) => this.section.removeEventListener(type, this.onFirstTouch, { passive: true }));
        this.touchEvents = null;
      }

      sync() {
        let active = this.trigger.style.display !== 'none';
        const idInput = this.form.querySelector('input[name="id"]');
        if (active && idInput && !idInput.value) {
          this.trigger.style.display = 'none';
          active = false;
        }
        this.section.classList.toggle('at-bis-active', active);
        this.section.dispatchEvent(new CustomEvent('at:bis', { bubbles: true, detail: { active, trigger: this.trigger } }));
        if (active && this.openPending) {
          this.openPending = false;
          this.trigger.click();
        }
      }

      onFirstTouch(event) {
        if (!event.target.closest || !event.target.closest('variant-selects, .at-sizes')) return;
        this.removeTouchListeners();
        this.load();
      }

      // The sold-out phone bar can promise Notify me before onsite.js arrives. Remember that tap and open when
      // Klaviyo reveals its trigger, so she does not have to tap again after the download.
      onNotify(event) {
        const pending = event.target.closest('[data-at-buybar-oos][data-bis="pending"]');
        const trigger = event.target.closest('.klaviyo-bis-trigger');
        if (!pending && !trigger) return;
        if (window.atKlaviyo) window.atKlaviyo.load();
        if (!pending && window.backInStockInitialized) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openPending = true;
        this.load();
      }

      // The theme's klaviyo.js (site-wide, for sign-up forms and tracking) does not define klaviyoBIS,
      // so back in stock still needs onsite.js. Both are separate files; neither loads the other. One onsite.js per
      // page: a klaviyoBIS that is already there is reused, and a load in flight is shared by every instance.
      // The script's outcome is kept on it (data-at-done), so an instance that finds it already finished still hears.
      load() {
        if (this.loading) return;
        this.loading = true;
        if (window.klaviyoBIS) return this.init();
        const src = 'https://a.klaviyo.com/media/js/onsite/onsite.js';
        let script = document.querySelector(`script[src="${src}"]`);
        if (script && script.dataset.atDone) return this.init();
        if (!script) {
          script = document.createElement('script');
          script.src = src;
          script.async = true;
          document.head.appendChild(script);
        }
        script.addEventListener('load', () => { script.dataset.atDone = 'load'; this.init(); }, { once: true });
        script.addEventListener('error', () => { script.dataset.atDone = 'error'; this.fail(); }, { once: true });
      }

      // Back in stock cannot start: onsite.js was blocked or failed (a content blocker, offline), or it loaded without
      // klaviyoBIS. Say so, so a sold-out product's phone bar stops promising "Notify me" (assets/at-product.js onBis).
      fail() {
        this.openPending = false;
        this.section.dispatchEvent(new CustomEvent('at:bis', { bubbles: true, detail: { active: false, failed: true, trigger: this.trigger } }));
      }

      init() {
        if (!window.klaviyoBIS) return this.fail();
        if (window.backInStockInitialized) return;
        window.backInStockInitialized = true;
        const d = this.dataset;
        window.klaviyoBIS.init({ account: d.account, platform: 'shopify' });
        window.klaviyoBIS.enable('backinstock', {
          trigger: {
            product_page_text: d.triggerText,
            product_page_class: 'at-btn wide',
            product_page_text_align: 'center',
            product_page_margin: '0px',
            replace_anchor: false,
          },
          modal: {
            headline: '{product_name}',
            body_content: d.body,
            email_field_label: d.emailLabel,
            button_label: d.buttonLabel,
            subscription_success_label: d.success,
            footer_content: '',
            additional_styles: this.styles(),
            font_family: 'Figtree, Arial, sans-serif',
            headers_font_family: 'Juana, Georgia, serif',
            drop_background_color: '#3c2f3a',
            background_color: '#fffdfd',
            text_color: '#3c3f53',
            button_text_color: '#fff',
            button_background_color: '#945663',
            close_button_color: '#3c3f53',
            error_background_color: '#fcd6d7',
            error_text_color: '#c72e2f',
            success_background_color: '#f7f3f4',
            success_text_color: '#3c3f53',
          },
        });
      }

      // The modal is an iframe, so the fonts and tokens are declared again for it.
      styles() {
        const d = this.dataset;
        return (
          d.fontFaces +
          'body{font:400 15px/1.55 Figtree,Arial,sans-serif;color:#3c3f53}' +
          '#klaviyo-bis-modal{width:calc(100% - 32px);max-width:440px;margin:8vh auto}' +
          '#container{position:relative;margin:0;padding:36px 32px 32px;background:#fffdfd;border:1px solid #e8e1e4;border-radius:0;box-shadow:0 30px 60px #3c2f3a14}' +
          '.modal-title{font:300 28px/1.15 Juana,Georgia,serif;letter-spacing:-.02em;color:#3a3737;margin:0 0 12px}' +
          '#container>p{margin:0 0 22px;color:#4f4a4e}' +
          '.form-control{font-family:inherit;font-size:15px;color:#3c3f53;height:50px;border:1px solid #d9d1d5;border-radius:0;box-shadow:none}' +
          '.form-control:focus{border-color:#3c3f53;outline:0;box-shadow:none}' +
          '.btn{min-height:50px;padding:0 28px;background:#945663;border:1px solid #945663;color:#fff;font:500 12px/1 Figtree,Arial,sans-serif;letter-spacing:.12em;text-transform:uppercase;border-radius:0;white-space:normal}' +
          '.btn:hover,.btn:active,.btn:focus{background:#7d4655;border-color:#7d4655;color:#fff}' +
          '.btn:focus-visible,.close:focus-visible{outline:2px solid #c69eb1;outline-offset:3px}' +
          '.close{color:#3c3f53;font-weight:300;text-shadow:none;top:10px;right:14px;opacity:1}' +
          '.restock-sizes{border:0;padding:0;margin:0 0 18px;min-width:0}' +
          '.restock-sizes legend{font-size:13px;color:#3c3f53;margin-bottom:10px}' +
          '.restock-size-options{display:flex;flex-wrap:wrap}' +
          '.restock-size{position:relative;margin:0 8px 8px 0;cursor:pointer;font-weight:400}' +
          '.restock-size input{position:absolute;opacity:0;width:1px;height:1px}' +
          '.restock-size span{display:inline-flex;align-items:center;justify-content:center;min-width:46px;min-height:46px;padding:0 10px;border:1px solid #d9d1d5;font-size:14px;color:#3c3f53;background:transparent}' +
          '.restock-size:hover span{border-color:#3c3f53}' +
          '.restock-size input:checked+span{background:#3c3f53;border-color:#3c3f53;color:#fff}' +
          '.restock-size input:focus-visible+span{outline:2px solid #c69eb1;outline-offset:2px}' +
          '@media (max-width:499px){#container{padding:32px 22px 24px}.modal-title{font-size:24px}}'
        );
      }

      // Once the modal opens, rebuild its variant <select> as size pills (same as the live theme). Klaviyo builds the
      // iframe in the same click, so the rebuild waits one task (a zero-delay yield, not a timer on the user).
      onOpen(event) {
        if (!event.target.closest || !event.target.closest('.klaviyo-bis-trigger')) return;
        setTimeout(() => {
          const frame = document.getElementById('klaviyo-bis-iframe');
          const doc = frame && frame.contentDocument;
          const select = doc && doc.getElementById('variants');
          if (!select || doc.querySelector('.restock-sizes')) return;
          const group = doc.createElement('fieldset');
          group.className = 'restock-sizes';
          const legend = doc.createElement('legend');
          legend.textContent = this.dataset.legend;
          group.appendChild(legend);
          const options = doc.createElement('div');
          options.className = 'restock-size-options';
          Array.prototype.forEach.call(select.options, (option) => {
            const label = doc.createElement('label');
            label.className = 'restock-size';
            const input = doc.createElement('input');
            input.type = 'radio';
            input.name = 'restock-variant';
            input.value = option.value;
            input.checked = option.selected;
            input.addEventListener('change', () => {
              if (input.checked) select.value = input.value;
            });
            const text = doc.createElement('span');
            text.textContent = option.textContent;
            label.appendChild(input);
            label.appendChild(text);
            options.appendChild(label);
          });
          group.appendChild(options);
          select.after(group);
          select.style.display = 'none';
        }, 0);
      }
    }
  );
}
